import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from "react";
import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/70 backdrop-blur-sm",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      // text-[var(--color-foreground)] inherits theme-aware foreground
      // (#0f172a light, #e2e8f0 dark) for the entire dialog tree, so
      // titles + body text are always legible. Without this, h3 inside
      // the portal inherits from <body> only in some browser/portal
      // path orderings — Safari notably renders black-on-dark in our
      // dark theme.
      //
      // No onPointerDownOutside / onEscapeKeyDown handlers: Radix's
      // defaults are already "close on outside click + Esc" for modal
      // dialogs. Keeping them un-prevented is the intended behavior.
      className={cn(
        "fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] rounded-lg p-6 cd-panel cd-enter text-[var(--color-foreground)]",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none">
        <X className="h-4 w-4" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />;
}

// Title/Description wrap the RADIX primitives, not bare <h3>/<p>.
//
// Radix wires `aria-labelledby`/`aria-describedby` on DialogContent by having
// its Title/Description register their generated ids on mount. A hand-rolled
// <h3> never registers, so EVERY dialog in this app shipped with no accessible
// name — announced to a screen reader as an unlabelled group — and logged
// "`DialogContent` requires a `DialogTitle`" plus a missing-description warning
// on each open. Swapped 2026-08-05.
//
// `asChild` keeps the rendered markup exactly as before (h3 / p), so this is an
// accessibility + console fix with no visual or DOM-shape change.

function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  // Explicit text-[var(--color-foreground)] in addition to the DialogContent
  // inherit — h3 user-agent styles set color in some browsers, overriding
  // the parent inherit. Belt-and-suspenders against black-on-dark.
  return (
    <DialogPrimitive.Title asChild>
      <h3
        className={cn(
          "text-lg font-semibold leading-none tracking-tight text-[var(--color-foreground)]",
          className,
        )}
        {...props}
      />
    </DialogPrimitive.Title>
  );
}

function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <DialogPrimitive.Description asChild>
      <p className={cn("text-sm text-muted-foreground", className)} {...props} />
    </DialogPrimitive.Description>
  );
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6", className)} {...props} />;
}

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
};
